import { useEffect, useMemo } from "react";
import { uniqBy } from "lodash";
import { Space } from "@highnote/server/src/core/entities";
import type { EntityRowConfig } from "App/components/EntityTable";
import { ENTITY_TYPE } from "App/components/EntityTable/config";
import { useDiscoverPageItems } from "App/routes/Main/Discover/useDiscoverPageItems";
import type {
  DiscoverPageItem,
  DiscoverPageItemDetail,
} from "@highnote/server/src/functions/handlers/discover/types";

/**
 * Builds the "no query" default rows for the Highnote tab of EntitySearch.
 *
 * Triggers the Discover items index + per-item-detail fetches via the
 * provider, then reads everything from provider state. Returns
 * `loading: true` until every detail is in the cache, so the caller can
 * gate the dialog on readiness and avoid showing thin (image + slug only)
 * rows.
 *
 * Discover items aren't real Space entities — they're CMS rows carrying a
 * coverImage URL, title, summary, entityId, and shareKey. To plug them into
 * the `SearchEntityList` pipeline (which expects `EntityRowConfig` rows
 * formed from `Space` entities), we cast a minimal Space-shaped object. The
 * shape is enough for `EntityPreview` to render a row, and for EntitySearch
 * to derive a shareKey for the click-through URL — `readableByV2` is set to
 * `[item.shareKey]` so the shared `derivedShareKey(space)` helper can find
 * it the same way it does for Algolia hits.
 */
export const useDiscoverPageItemsAsDefaultRows = (): {
  rows: EntityRowConfig[];
  loading: boolean;
} => {
  const { items, loadItems, itemDetails } = useDiscoverPageItems();

  // Kick off the Discover items index fetch on mount. The provider dedupes,
  // so this is a no-op if the user has already visited /home. The provider
  // also cascades into per-item detail fetches, so we don't need to
  // schedule those here.
  useEffect(() => {
    loadItems();
  }, [loadItems]);

  // Flatten + dedupe Space items from every section/tab.
  const spaceItems = useMemo(() => {
    if (!items) return [];
    const all: DiscoverPageItem[] = [];
    items.sections.forEach((section) => {
      section.items.forEach((item) => {
        if (item.entityType === "Space" && item.entityId) all.push(item);
      });
      section.tabs?.forEach((tab) => {
        tab.items.forEach((item) => {
          if (item.entityType === "Space" && item.entityId) all.push(item);
        });
      });
    });
    return uniqBy(all, (item) => item.entityId);
  }, [items]);

  const allLoaded = spaceItems.every(
    (item) => itemDetails[item.recordId]?.loading === false,
  );

  const rows = useMemo(() => {
    return spaceItems.map((item) =>
      formatDiscoverPageItemAsSpaceRow({
        item,
        detail: itemDetails[item.recordId]?.data ?? null,
      }),
    );
  }, [spaceItems, itemDetails]);

  return { rows, loading: !items || !allLoaded };
};

const formatDiscoverPageItemAsSpaceRow = ({
  item,
  detail,
}: {
  item: DiscoverPageItem;
  detail: DiscoverPageItemDetail | null;
}): EntityRowConfig => {
  const discoverPageItemAsSpace = {
    id: item.entityId!,
    name: detail?.title ?? item.slug,
    createdByName: detail?.createdByName ?? null,
    // detail.memberCount is null only for User discover items; this helper
    // is space-only, so in practice it's a number once the detail loads.
    // Leave undefined while loading so EntityPreview's own fallback decides
    // how to render the missing state.
    memberCount: detail?.memberCount ?? undefined,
    artworkUrl: detail?.squareImageUrl ?? item.coverImageUrl ?? undefined,
    // Discover items are curated for the public Highnote feed by definition.
    // Stamp visibility on the synthesized Space so it passes EntitySearch's
    // Highnote-tab filter (`space.visibility === "visible"`).
    visibility: "visible",
    // Expose the share key on `readableByV2` so EntitySearch's
    // `derivedShareKey` helper finds it via the same path used for Algolia
    // public-search hits. (Real Spaces store share keys as `share_<uuid>`
    // entries in `readableByV2`; we mirror that here.)
    readableByV2: item.shareKey ? [item.shareKey] : [],
  } as Space;

  return {
    key: item.recordId,
    id: item.entityId!,
    entity: discoverPageItemAsSpace,
    type: ENTITY_TYPE.SPACE,
    name: discoverPageItemAsSpace.name,
    // `onClick` is required to mark the row as "enabled" — SearchEntityList
    // only routes clicks through `tableBaseProps.onSelect` for rows that
    // already have an onClick. The actual navigation happens in
    // EntitySearch.onSelect; this placeholder gets overwritten.
    onClick: () => undefined,
  } as EntityRowConfig;
};
